import React, { Fragment, Suspense, useEffect, useRef, useState } from "react"
import Header from "../Layouts/Header/Header"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { openWebsite } from "../redux/actions"
import { selectWebsites } from "../redux/auth/selectors"
import { selectWebsiteData } from "../redux/general/selectors"
import { Helmet } from "react-helmet"
import { usePinnedSidebar } from "../context/PinnedSidebarContext"
import { useRoutePermission } from "../context/RoutePermissionContext"
import ErrorFallback from "../ErrorBoundaries/ErrorFallback"
import { injectCustomCss } from "../utils/injectCustomCss"
import DynamicComponentRenderer from "../screens/DynamicReactComponent/DynamicComponentRenderer"
import "./Sidebar/expanded_sidebar.css"
import "./Sidebar/sidebar_full_screen.css"

const FullPreview = ({ children }) => {
    const { isPinned } = usePinnedSidebar()
    const websiteData = useSelector(selectWebsiteData)
    const useExpandedSidebar = websiteData?.sidebar_type === "modern"

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
        () => localStorage.getItem("expandedSidebarCollapsed") === "true",
    )

    useEffect(() => {
        /**
         * Listens for sidebar collapse state changes via storage events and a custom event.
         */
        const handleStorageChange = () => {
            setIsSidebarCollapsed(localStorage.getItem("expandedSidebarCollapsed") === "true")
        }
        window.addEventListener("storage", handleStorageChange)
        window.addEventListener("sidebarCollapseToggle", handleStorageChange)
        return () => {
            window.removeEventListener("storage", handleStorageChange)
            window.removeEventListener("sidebarCollapseToggle", handleStorageChange)
        }
    }, [])

    useEffect(() => {
        const handleResize = () => {
            document.getElementById("outer-wrapper").style.height =
                `${window.innerHeight - document.getElementById("header").clientHeight}px`
        }
        window.addEventListener("resize", handleResize)
        handleResize()

        const mainLayoutElement = document.getElementById("main-layout")
        if (["/routing", "/stations"].includes(window.location.pathname)) {
            mainLayoutElement.classList.remove("container-fluid")
        } else {
            mainLayoutElement.classList.add("container-fluid")
        }

        return () => window.removeEventListener("resize", handleResize)
    }, [window.location.pathname])

    return (
        <>
            <div className="sticky">
                {useExpandedSidebar ? (
                    <DynamicComponentRenderer
                        componentKey="expanded-sidebar"
                        componentName="expanded-sidebar"
                        appKey="master_v2"
                    />
                ) : (
                    <DynamicComponentRenderer
                        componentKey="collapsed-sidebar"
                        componentName="collapsed-sidebar"
                        appKey="master_v2"
                    />
                )}
            </div>
            <div
                className={`main-content app-content-full-view mt-0 ${useExpandedSidebar ? `e-expanded-sidebar-main${isSidebarCollapsed ? " e-sidebar-collapsed" : ""}` : ""}`}
                style={{
                    position: "relative",
                    height: "100%",
                    marginLeft: useExpandedSidebar ? undefined : isPinned ? "6.5rem" : "0rem",
                }}
            >
                <div id="outer-wrapper" className="outer-wrapper">
                    {children}
                </div>
                <div
                    id="main-layout"
                    className="main-container container-fluid"
                    style={{ padding: "0px" }}
                />
            </div>
        </>
    )
}

const Layout = ({ children, fullView }) => {
    const Websites = useSelector(selectWebsites)
    const websiteData = useSelector(selectWebsiteData)
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate()
    const { isUnauthorized } = useRoutePermission()
    const location = useLocation()

    const expectedAppKey = sessionStorage.getItem("app_key")
    const expectedAppId = sessionStorage.getItem("app")
    const isAppMismatch = expectedAppKey && websiteData?.app_key !== expectedAppKey
    const lastOpenedAppKeyRef = useRef(null)

    useEffect(() => {
        const website = Websites.find((w) => w.id == sessionStorage.getItem("app"))
        if (!expectedAppKey || !website) return

        // Avoid duplicate permission API calls when effects re-run.
        if (lastOpenedAppKeyRef.current === expectedAppKey) return

        if (website.app_key !== expectedAppKey) return

        lastOpenedAppKeyRef.current = expectedAppKey
        dispatch(openWebsite(website))
        setLoading(false)
    }, [Websites])

    // Prevent "previous app" flash when navigating to a route that React keeps mounted.
    // sessionStorage is updated before navigate(), so we can gate rendering until
    // Redux (websiteData) catches up to the expected app key.
    useEffect(() => {
        if (!expectedAppKey) return

        if (websiteData?.app_key !== expectedAppKey) {
            setLoading(true)

            if (expectedAppId) {
                const website = Websites.find((w) => w.id == expectedAppId)
                if (website && website.app_key === expectedAppKey) {
                    if (lastOpenedAppKeyRef.current !== expectedAppKey) {
                        lastOpenedAppKeyRef.current = expectedAppKey
                        dispatch(openWebsite(website))
                    }
                }
            }
            return
        }

        setLoading(false)
    }, [
        location.pathname,
        location.search,
        expectedAppKey,
        expectedAppId,
        websiteData?.app_key,
        Websites,
        dispatch,
    ])

    useEffect(() => {
        if (!sessionStorage.getItem("app_key")) {
            navigate("/")
        }
    }, [])

    useEffect(() => {
        injectCustomCss()
    }, [])

    const appTitle = process.env.REACT_APP_TITLE

    return (
        <Fragment>
            <Helmet>
                <title>{appTitle}</title>
            </Helmet>
            <Suspense fallback="">
                <div className="horizontalMenuWrapper">
                    <div className="page">
                        <div className="page-main">
                            {!loading && !isAppMismatch && (
                                <>
                                    {isUnauthorized ? (
                                        <ErrorFallback />
                                    ) : (
                                        <>
                                            <Header fullView={fullView} />
                                            <FullPreview fullView={fullView}>
                                                {children}
                                            </FullPreview>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Suspense>
        </Fragment>
    )
}

export default Layout
